import { Component, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { RtI18nDirective } from '../runtime-translation.directive';
import { CommunicationConsentItem } from '../../../../services/communication-consent.service';

/**
 * The per-school communication-consent disclosure + checkboxes (Marketing / Operations / an explicit
 * decline) shared by the first-login communication-preferences dialog, the profile page's read-only
 * summary card, and the consent edit dialog, so the legal verbiage lives in exactly one template.
 * The decline box is UI-only — a declined school is simply saved with both consents false — but it
 * lets every school carry an explicit answer (see allAnswered()). The rows are edited in place;
 * hosts own persistence.
 */
@Component({
  selector: 'app-communication-consent-fields',
  standalone: true,
  templateUrl: './communication-consent-fields.component.html',
  styleUrl: './communication-consent-fields.component.scss',
  imports: [FormsModule, MatCheckboxModule, RtI18nDirective]
})
export class CommunicationConsentFieldsComponent implements OnChanges {
  @Input({ required: true }) schools: CommunicationConsentItem[] = [];
  /**
   * Treat schools loaded with neither consent as previously declined (pre-checks the decline box).
   * Used on the profile page, where every school has already been answered once; the first-login
   * dialog leaves it off because an unanswered school is exactly what that dialog must collect.
   */
  @Input() presumeDeclined = false;
  /** Disable the checkboxes: display-only summary (the profile card; clicks fall through to it). */
  @HostBinding('class.readonly') @Input() readonly = false;
  /** Show the inline "make a selection" error under unanswered schools. */
  @Input() showErrors = false;
  /** Fires on any checkbox change; hosts use it to autosave or revalidate. */
  @Output() changed = new EventEmitter<void>();

  private declinedSchoolIds = new Set<number>();

  ngOnChanges(changes: SimpleChanges): void {
    // Reseed whenever the host swaps in a fresh list (e.g. the profile card after the edit dialog
    // saves); in-place edits inside this component don't retrigger this.
    if (changes['schools'] && this.presumeDeclined) {
      this.declinedSchoolIds = new Set(
        this.schools.filter(s => !s.Marketing && !s.Operations).map(s => s.SchoolId)
      );
    }
  }

  isDeclined(school: CommunicationConsentItem): boolean {
    return this.declinedSchoolIds.has(school.SchoolId);
  }

  isAnswered(school: CommunicationConsentItem): boolean {
    return school.Marketing || school.Operations || this.isDeclined(school);
  }

  allAnswered(): boolean {
    return this.schools.every(s => this.isAnswered(s));
  }

  // Decline is mutually exclusive with the two consents.
  onDeclineChange(school: CommunicationConsentItem, checked: boolean): void {
    if (checked) {
      this.declinedSchoolIds.add(school.SchoolId);
      school.Marketing = false;
      school.Operations = false;
    } else {
      this.declinedSchoolIds.delete(school.SchoolId);
    }
    this.changed.emit();
  }

  onConsentChange(school: CommunicationConsentItem, checked: boolean): void {
    if (checked) {
      this.declinedSchoolIds.delete(school.SchoolId);
    }
    this.changed.emit();
  }
}
